import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus.svg';
import { FilterPopover, Flex } from '@elementary/common-ui/components';
import FilterChipRow from '@elementary/common-ui/components/Table/TableFilters/FilterChipRow';
import { StreamSelector } from '@elementary/common-ui/components/lineageGraph';
import type {
  HopLimit,
  DagDepthBoundary,
} from '@elementary/common-ui/components/lineageGraph/types';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import {
  HOP_LIMIT,
  LINEAGE_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import { useLineageFilterOptions } from '../useLineageFilterOptions';

const Wrapper = styled(Flex)`
  background: ${({ theme }) => theme.palette.common.white};
  border: ${({ theme }) => `1px solid ${theme.palette.common.gray13}`};
  border-radius: 8px;
`;

const LeftSection = styled(Flex)`
  border-right: 1px solid ${({ theme }) => theme.palette.common.gray13};
  padding: 16px;
  gap: 16px;
`;

type Props = { dagDepthBoundary?: DagDepthBoundary };

const LineageFilters = ({ dagDepthBoundary }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialFilterRun = useRef(true);
  const prevSelectionKey = useRef<string | undefined>(undefined);

  const { selectedTreeNode, selectedModelColumn } = useSelectedTreeItem();

  const { urlFilters, addFilter, clearFilter, clearAllFilters, updateFilter } =
    useUrlFilters({
      urlKey: LINEAGE_FILTERS,
    });

  //TODO: `job` and `biNodeType` filters are not wired yet.
  const filterOptions = useLineageFilterOptions();

  const selectedHopLimit = useMemo(() => {
    const param = searchParams.get(HOP_LIMIT);
    return (
      param
        ? JSON.parse(param)
        : {
            upstream: dagDepthBoundary?.incomingDepth ? 1 : 0,
            downstream: dagDepthBoundary?.outgoingDepth ? 1 : 0,
          }
    ) as HopLimit;
  }, [dagDepthBoundary, searchParams]);

  const handleHopFilterChange = useCallback(
    (val?: HopLimit) =>
      val
        ? setSearchParams((prev) => {
            prev.set(HOP_LIMIT, JSON.stringify(val));
            return prev;
          })
        : setSearchParams((prev) => {
            prev.delete(HOP_LIMIT);
            return prev;
          }),
    [setSearchParams],
  );

  useEffect(() => {
    if (isInitialFilterRun.current) {
      isInitialFilterRun.current = false;
      return;
    }
    // setTimeout solves an issue when hop limit is not cleared when applying filters
    setTimeout(() => {
      setSearchParams((prev) => {
        prev.delete(HOP_LIMIT);
        return prev;
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(urlFilters)]);

  useEffect(() => {
    const selectionKey =
      selectedModelColumn && selectedTreeNode?.id
        ? `column:${selectedTreeNode.id}:${selectedModelColumn}`
        : selectedModelColumn
        ? `column:${selectedModelColumn}`
        : selectedTreeNode?.id
        ? `node:${selectedTreeNode.id}`
        : undefined;
    const previousKey = prevSelectionKey.current;
    prevSelectionKey.current = selectionKey;

    if (
      previousKey === undefined ||
      selectionKey === undefined ||
      previousKey === selectionKey
    ) {
      return;
    }

    // setTimeout solves an issue when lineage filters and hop limit are not cleared when changing selected tree item
    setTimeout(() => {
      setSearchParams((prev) => {
        prev.delete(LINEAGE_FILTERS);
        prev.delete(HOP_LIMIT);
        return prev;
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreeNode?.id, selectedModelColumn]);

  return (
    <Wrapper>
      <LeftSection>
        <Tooltip
          title={
            selectedModelColumn &&
            'Lineage is filtered on a specific column, additional lineage filters are not supported'
          }
        >
          <div>
            <FilterPopover
              filterOptions={filterOptions}
              onSubmit={(val) => {
                const newValue = (val.value || val.prevValue).filter(
                  (v) => v !== 'all',
                );

                if (val.prevValue?.length || val.prevValue?.includes('all')) {
                  updateFilter(
                    val.property,
                    val.operator,
                    val.prevValue.filter((v) => v !== 'all'),
                    newValue,
                  );
                } else {
                  addFilter(val.property, val.operator, newValue);
                }
              }}
              disabled={!!selectedModelColumn || filterOptions.length === 0}
              buttonProps={{ variant: 'contained', startIcon: <PlusIcon /> }}
              popoverProps={{ anchorOrigin: { vertical: 70, horizontal: -17 } }}
            />
          </div>
        </Tooltip>

        {urlFilters && (
          <FilterChipRow
            filterOptions={filterOptions}
            selectedFilters={urlFilters}
            clearFilter={clearFilter}
            onClearAll={clearAllFilters}
            onSubmit={(val) =>
              updateFilter(
                val.property,
                val.operator,
                val.prevValue!,
                val.value,
                val.index,
              )
            }
          />
        )}
      </LeftSection>
      <Flex sx={{ p: 2 }}>
        <StreamSelector
          hopLimit={selectedHopLimit}
          onHopLimitChange={handleHopFilterChange}
          dagDepthBoundary={dagDepthBoundary}
        />
      </Flex>
    </Wrapper>
  );
};

export default LineageFilters;
